import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaStore, FaBuilding, FaSpa, FaLightbulb, 
  FaBalanceScale, FaShoppingCart, FaArrowRight
} from 'react-icons/fa';
import { Filter } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import './Portfolio.css';

const Portfolio = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('All');

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0.9,
      transition: { duration: 0.2 }
    }
  };

  const projects = [
    {
      id: 1,
      title: t('portfolio.projects.0.title', 'Restaurant "De Gouden Lepel"'),
      category: t('portfolio.projects.0.category', 'Horeca'),
      type: 'Business',
      description: t('portfolio.projects.0.description', 'Modern restaurant website met online reserveringssysteem en digitale menukaart.'),
      icon: FaStore,
      gradient: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
      features: [
        t('portfolio.projects.0.features.0', 'Online reserveren'), 
        t('portfolio.projects.0.features.1', 'Digitale menukaart'), 
        t('portfolio.projects.0.features.2', 'Instagram integratie')
      ],
      imageColor: '#fff7ed'
    },
    {
      id: 2,
      title: t('portfolio.projects.1.title', 'Bouwbedrijf Van der Berg'),
      category: t('portfolio.projects.1.category', 'Bouw'),
      type: 'Business',
      description: t('portfolio.projects.1.description', 'Professionele bedrijfswebsite met projectportfolio en contactformulier.'),
      icon: FaBuilding,
      gradient: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
      features: [
        t('portfolio.projects.1.features.0', 'Project showcase'), 
        t('portfolio.projects.1.features.1', 'Contact formulier'), 
        t('portfolio.projects.1.features.2', 'SEO optimalisatie')
      ],
      imageColor: '#f0f9ff'
    },
    {
      id: 3,
      title: t('portfolio.projects.2.title', 'Yoga Studio Zen'),
      category: t('portfolio.projects.2.category', 'Wellness'),
      type: 'Starter',
      description: t('portfolio.projects.2.description', 'Kalme en overzichtelijke website met lesrooster en inschrijvingssysteem.'),
      icon: FaSpa,
      gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
      features: [
        t('portfolio.projects.2.features.0', 'Lesrooster'), 
        t('portfolio.projects.2.features.1', 'Online inschrijvingen'), 
        t('portfolio.projects.2.features.2', 'Blog functie')
      ],
      imageColor: '#f5f3ff'
    },
    {
      id: 4,
      title: t('portfolio.projects.3.title', 'Tech Startup "InnovateLab"'),
      category: t('portfolio.projects.3.category', 'Technology'),
      type: 'Premium',
      description: t('portfolio.projects.3.description', 'Geavanceerde website met Strapi CMS voor dynamisch contentbeheer.'),
      icon: FaLightbulb,
      gradient: 'linear-gradient(135deg, #eab308 0%, #ca8a04 100%)',
      features: [
        t('portfolio.projects.3.features.0', 'Headless CMS'), 
        t('portfolio.projects.3.features.1', 'Animaties'), 
        t('portfolio.projects.3.features.2', 'API integraties')
      ],
      imageColor: '#fefce8'
    },
    {
      id: 5,
      title: t('portfolio.projects.4.title', 'Advocatenkantoor Jansen'),
      category: t('portfolio.projects.4.category', 'Juridisch'),
      type: 'Business',
      description: t('portfolio.projects.4.description', 'Zakelijke website met teamoverzicht en praktijkgebieden.'),
      icon: FaBalanceScale,
      gradient: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
      features: [
        t('portfolio.projects.4.features.0', 'Team pagina'), 
        t('portfolio.projects.4.features.1', 'Case studies'), 
        t('portfolio.projects.4.features.2', 'Contactformulier')
      ],
      imageColor: '#f8fafc'
    },
    {
      id: 6,
      title: t('portfolio.projects.5.title', 'E-commerce "GreenLife"'),
      category: t('portfolio.projects.5.category', 'Webshop'),
      type: 'Premium',
      description: t('portfolio.projects.5.description', 'Complete webshop voor duurzame producten met betalingssysteem.'),
      icon: FaShoppingCart,
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      features: [
        t('portfolio.projects.5.features.0', 'Webshop'), 
        t('portfolio.projects.5.features.1', 'Betalingen'), 
        t('portfolio.projects.5.features.2', 'Voorraadbeheer')
      ],
      imageColor: '#ecfdf5'
    }
  ];

  const filteredProjects = filter === 'All' 
    ? projects 
    : projects.filter(p => p.type === filter);

  const filters = ['All', 'Starter', 'Business', 'Premium'];

  return (
    <div className="portfolio">
      <SEO 
        title={t('portfolio.seo_title', 'Portfolio - Onze Projecten')}
        description={t('portfolio.seo_desc', 'Bekijk ons portfolio met recente projecten. Van webshops tot bedrijfswebsites, wij leveren kwaliteit en maatwerk.')}
        canonical="/portfolio"
      />
      {/* Hero Section */}
      <section className="portfolio-hero">
        <div className="container">
          <div className="hero-grid">
            <motion.div 
              className="hero-content"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="page-title">
                {t('portfolio.hero_title_1', 'Ons Werk,')}<br />
                <span className="text-gradient">{t('portfolio.hero_title_2', 'Jouw Inspiratie')}</span>
              </h1>
              <p className="page-subtitle">
                {t('portfolio.hero_subtitle', 'Bekijk een selectie van onze recente projecten. Van strakke onepagers tot uitgebreide webshops en maatwerk applicaties.')}
              </p>
              <div className="hero-buttons">
                <Link to="/contact" className="btn btn-primary">
                  {t('portfolio.cta_start', 'Start Jouw Project')}
                </Link>
                <button onClick={() => document.getElementById('projects').scrollIntoView({ behavior: 'smooth' })} className="btn btn-secondary">
                  {t('portfolio.cta_view', 'Bekijk Portfolio')}
                </button>
              </div>
            </motion.div>

            <motion.div 
              className="hero-visual"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
            >
              <svg viewBox="0 0 600 500" className="hero-svg">
                <defs>
                  <linearGradient id="portfolioGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#fff7ed" />
                    <stop offset="100%" stopColor="#ffedd5" />
                  </linearGradient>
                  <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="0" dy="10" stdDeviation="10" floodColor="#000000" floodOpacity="0.1" />
                  </filter>
                </defs>

                <circle cx="300" cy="250" r="220" fill="url(#portfolioGrad)" opacity="0.6" />

                {/* Main Browser Window */}
                <motion.g
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <rect x="100" y="120" width="400" height="260" rx="12" fill="white" filter="url(#shadow)" />
                  {/* Browser Header */}
                  <rect x="100" y="120" width="400" height="30" rx="12" fill="#f1f5f9" />
                  <rect x="100" y="140" width="400" height="10" fill="#f1f5f9" />
                  <circle cx="120" cy="135" r="4" fill="#ef4444" />
                  <circle cx="135" cy="135" r="4" fill="#fbbf24" />
                  <circle cx="150" cy="135" r="4" fill="#10b981" />
                  
                  {/* Content Grid */}
                  <rect x="120" y="170" width="170" height="100" rx="8" fill="#f8fafc" stroke="#e2e8f0" />
                  <rect x="310" y="170" width="170" height="100" rx="8" fill="#f8fafc" stroke="#e2e8f0" />
                  <rect x="120" y="290" width="170" height="70" rx="8" fill="#f8fafc" stroke="#e2e8f0" />
                  <rect x="310" y="290" width="170" height="70" rx="8" fill="#f8fafc" stroke="#e2e8f0" />
                  
                  {/* Animated Selection Frame */}
                  <motion.rect 
                    x="115" y="165" width="180" height="110" rx="10" 
                    fill="none" stroke="#f97316" strokeWidth="2"
                    animate={{ 
                      x: [115, 305, 115],
                      y: [165, 165, 285, 165]
                    }}
                    transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                  />
                </motion.g>

                {/* Floating Elements */}
                <motion.g
                  animate={{ y: [-10, 10, -10] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                >
                  <rect x="450" y="80" width="100" height="80" rx="12" fill="white" filter="url(#shadow)" />
                  <circle cx="500" cy="120" r="20" fill="#f97316" opacity="0.1" />
                  <path d="M490 120 L500 130 L510 110" stroke="#f97316" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </motion.g>

                <motion.g
                  animate={{ y: [10, -10, 10] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                >
                  <rect x="50" y="300" width="120" height="90" rx="12" fill="white" filter="url(#shadow)" />
                  <rect x="70" y="320" width="80" height="8" rx="4" fill="#e2e8f0" />
                  <rect x="70" y="335" width="60" height="8" rx="4" fill="#e2e8f0" />
                  <rect x="70" y="350" width="40" height="8" rx="4" fill="#f97316" />
                </motion.g>

              </svg>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="projects-section section">
        <div className="container">
          
          {/* Filter Bar */}
          <div className="filter-container">
            <div className="filter-label">
              <Filter size={18} />
              <span>{t('portfolio.filter_label', 'Filter op pakket:')}</span>
            </div>
            <div className="filter-buttons">
              {filters.map((f) => (
                <button
                  key={f}
                  className={`filter-btn ${filter === f ? 'active' : ''}`}
                  onClick={() => setFilter(f)}
                >
                  {f === 'All' ? t('portfolio.filters.all', 'Alles') : f}
                </button>
              ))}
            </div>
          </div>

          <motion.div 
            className="projects-grid"
            layout
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <AnimatePresence mode='popLayout'>
              {filteredProjects.map((project) => {
                const Icon = project.icon;
                return (
                  <motion.div 
                    key={project.id}
                    className="project-card"
                    layout
                    variants={itemVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    whileHover={{ y: -10 }}
                  >
                    <div className="project-image-container" style={{ background: project.imageColor }}>
                      <div className="project-overlay">
                        <button className="view-project-btn">
                          {t('portfolio.view_project', 'Bekijk Project')} <FaArrowRight />
                        </button>
                      </div>
                      <div className="project-mockup">
                        <div className="mockup-header">
                          <div className="mockup-dot red"></div>
                          <div className="mockup-dot yellow"></div>
                          <div className="mockup-dot green"></div>
                        </div>
                        <div className="mockup-content">
                          <div className="mockup-icon-wrapper" style={{ background: project.gradient }}>
                            <Icon className="mockup-icon" />
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="project-content">
                      <div className="project-meta">
                        <span className="project-category">{project.category}</span>
                        <span className={`project-badge ${project.type.toLowerCase()}`}>
                          {project.type}
                        </span>
                      </div>
                      <h3 className="project-title">{project.title}</h3>
                      <p className="project-description">{project.description}</p>
                      
                      <div className="project-features">
                        {project.features.map((feature, index) => (
                          <span key={index} className="feature-tag">{feature}</span>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="portfolio-cta section">
        <div className="container">
          <motion.div 
            className="cta-box"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="cta-bg-pattern">
              <div className="cta-circle cta-circle-1"></div>
              <div className="cta-circle cta-circle-2"></div>
            </div>
            
            <div className="cta-content">
              <h2>{t('portfolio.cta_footer_title', 'Overtuigd van onze kwaliteit?')}</h2>
              <p>
                {t('portfolio.cta_footer_text', 'Laten we samen kijken hoe we uw bedrijf net zoprofessioneel op de kaart kunnen zetten.')}
              </p>
              <div className="cta-buttons">
                <Link to="/contact" className="btn btn-primary">
                  {t('portfolio.cta_footer_btn_primary', 'Vraag Offerte Aan')}
                </Link>
                <Link to="/diensten" className="btn btn-secondary">
                  {t('portfolio.cta_footer_btn_secondary', 'Bekijk Onze Diensten')}
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Portfolio;
